import { useQuery } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  addMember,
  createOrganisation,
  organisationFormQuery,
  updateOrganisation,
  type MemberAdded,
} from '../../api/settingsEaseetask'
import { Modal } from '../../ui/Modal'
import { SelectPicker } from '../../ui/SelectPicker'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'

/**
 * Formularele ecranului Settings -> Easeetask, camp cu camp dupa machetele
 * vechi: `organisations/createOrganisation.php`, `editOrganisation.php`
 * (acelasi formular, cu `organisation_id` si bifa "default" in plus) si
 * `addMembersForm.php`. In portal se deschideau in randul desfasurat
 * (`getActionTabs`); aici in fereastra.
 *
 * Validarea o face serverul, cu regulile lui `validateFields`; aici raman
 * numai `required`-urile din markup, ca in machete.
 */

function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

const EMPTY = { organisation_name: '', email: '', website: '', phone: '', address: '', description: '', private: false, default: false }

export function OrganisationModal({ id, onClose, onSaved }: { id: number; onClose(): void; onSaved(): void }) {
  const existing = useQuery(organisationFormQuery(id))
  const [form, setForm] = useState(EMPTY)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const o = existing.data?.organisation
    if (id > 0 && o) {
      setForm({
        organisation_name: o.organisation_name, email: o.email, website: o.website, phone: o.phone,
        address: o.address, description: o.description, private: o.private === 1, default: o.default === 1,
      })
    }
  }, [id, existing.data])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    const fields: Record<string, string> = {
      organisation_name: form.organisation_name, email: form.email, website: form.website,
      phone: form.phone, address: form.address, description: form.description,
    }
    /* Bifele nebifate nu intra in POST, ca in formularul vechi. */
    if (form.private) fields.private = 'on'
    if (id > 0 && form.default) fields.default = 'on'
    void (id > 0 ? updateOrganisation(id, fields) : createOrganisation(fields))
      .then((r) => {
        setBusy(false)
        toast(r.message, 'Organisation')
        onSaved()
      })
      .catch((err: unknown) => {
        setBusy(false)
        toast(failed(err), 'Organisation', 'error')
      })
  }

  const text = (name: keyof typeof EMPTY, label: string, type = 'text', required = false, placeholder = '') => (
    <div className="form-group">
      <label htmlFor={`org-${name}`}>
        {label}
        {required ? <span className="required">*</span> : null}
      </label>
      <input
        type={type}
        id={`org-${name}`}
        name={name}
        className="form-control"
        required={required}
        placeholder={placeholder}
        value={String(form[name])}
        onChange={(ev) => setForm({ ...form, [name]: ev.target.value })}
      />
    </div>
  )

  return (
    <Modal id="organisationModal" open onClose={onClose} title={id > 0 ? 'Edit organisation' : 'Create organisation'} labelledBy="organisationModalLabel">
      {id > 0 && !existing.data ? (
        existing.error ? <div className="alert alert-danger">{failed(existing.error)}</div> : <Spinner />
      ) : (
        <form method="post" className={`form-horizontal ${id > 0 ? 'update_organisation' : ''}`} id={id > 0 ? undefined : 'create_organisation'} onSubmit={submit}>
          {text('organisation_name', 'Name', 'text', true, 'Organisation Name')}
          {text('email', 'Email', 'email', false, 'Enter email')}
          {text('website', 'Website', 'text', false, 'https://www.example.com')}
          {text('phone', 'Phone')}
          {text('address', 'Address')}
          <div className="form-group">
            <label>
              <input type="checkbox" name="private" checked={form.private} onChange={(ev) => setForm({ ...form, private: ev.target.checked })} /> Private
            </label>
          </div>
          <div className="form-group">
            <label htmlFor="org-description">Description</label>
            <textarea id="org-description" name="description" className="form-control" rows={4} value={form.description} onChange={(ev) => setForm({ ...form, description: ev.target.value })} />
          </div>
          {id > 0 ? (
            <div className="form-group">
              <label>
                <input type="checkbox" name="default" checked={form.default} onChange={(ev) => setForm({ ...form, default: ev.target.checked })} /> Default organisation
              </label>
            </div>
          ) : null}
          <div className="form-group aright">
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>{' '}
            <button type="submit" className={`btn btn-theme ${id > 0 ? 'update_organisation' : 'create_organisation'}`} disabled={busy}>
              {id > 0 ? 'Update organisation' : 'Create organisation'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}

const ROLES = [
  { value: 'ROLE_MEMBER', label: 'Member' },
  { value: 'ROLE_ADMIN', label: 'Admin' },
]

export function AddMemberModal({ organisation, onClose, onSaved }: { organisation: number; onClose(): void; onSaved(): void }) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('ROLE_MEMBER')
  const [busy, setBusy] = useState(false)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    void addMember(organisation, { firstName, lastName, email, role })
      .then((r: MemberAdded) => {
        setBusy(false)
        toast(r.message, 'Add member', r.changed && !r.sent ? 'warning' : 'success')
        if (r.changed && !r.sent) toast('The invitation email could not be sent.', 'Add member', 'error')
        onSaved()
      })
      .catch((err: unknown) => {
        setBusy(false)
        toast(failed(err), 'Add member', 'error')
      })
  }

  return (
    <Modal id="addMemberModal" open onClose={onClose} title="Add member" labelledBy="addMemberModalLabel">
      <form method="post" className="form-horizontal add_member" onSubmit={submit}>
        <div className="form-group">
          <label htmlFor="firstName">
            First Name<span className="required">*</span>
          </label>
          <input type="text" name="firstName" id="firstName" className="form-control" required value={firstName} onChange={(ev) => setFirstName(ev.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="lastName">
            Last Name<span className="required">*</span>
          </label>
          <input type="text" id="lastName" name="lastName" className="form-control" required value={lastName} onChange={(ev) => setLastName(ev.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="member-email">
            Email<span className="required">*</span>
          </label>
          <input type="email" name="email" id="member-email" className="form-control" placeholder="Email Address" required value={email} onChange={(ev) => setEmail(ev.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">
            Role <span className="required">*</span>
          </label>
          <SelectPicker id="role" name="role" className="form-control selectpicker select-event" title="Role" options={ROLES} value={role} onChange={setRole} />
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-theme add_member" disabled={busy}>
            Add
          </button>
        </div>
      </form>
    </Modal>
  )
}
